import { useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { type ChainflipToken } from '@/shared/assets/tokens';
import { CustomNumberFlow } from '@/shared/components/CustomNumberFlow';
import Card from '@/shared/components/flip-ui-kit/Card';
import { LendingPoolsTable } from '@/shared/components/lending/LendingPoolsTable';
import { SupplyPosition } from '@/shared/components/lending/SupplyPosition';
import LoadingSpinner from '@/shared/components/LoadingSpinner';
import { useAllTimeLendingStats } from '@/shared/hooks/useAllTimeLendingStats';
import useBoolean from '@/shared/hooks/useBoolean';
import { useLendingPools } from '@/shared/hooks/useLendingPools';
import { useLpLendingPositions } from '@/shared/hooks/useLpLendingPositions';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import ArrowRightIcon from '@/shared/icons/ArrowRight';
import ArrowIcon from '@/shared/icons/flip-ui-kit/large/Arrow';
import BorrowIcon from '@/shared/icons/flip-ui-kit/large/Borrow';
import DocHistoryIcon from '@/shared/icons/flip-ui-kit/large/DocHistory';
import MoneyBagIcon from '@/shared/icons/flip-ui-kit/large/MoneyBag';
import { apyToText } from '@/shared/utils/boost';
import { chainflipAssetMap } from '@/shared/utils/env';
import { formatUsdValue } from '@/shared/utils/numbers';
import { RemoveSupplyModal } from './Modals/RemoveSupplyModal';
import { SupplyModal } from './Modals/SupplyModal';
import { LpFundAccountButton } from '../LpFundAccountButton';

const NoLendingPositions = ({
  openSupplyModal,
  apy,
  isLendingPoolsLoading,
  isAllTimeStatsLoading,
  totalSuppliedUsd,
  allTimeBorrowedUsd,
  totalFeesUsd,
}: {
  openSupplyModal: (token: ChainflipToken) => void;
  apy: number;
  isLendingPoolsLoading: boolean;
  isAllTimeStatsLoading: boolean;
  totalSuppliedUsd: number;
  allTimeBorrowedUsd: number;
  totalFeesUsd: number;
}) => (
  <>
    <div className="flex justify-between font-aeonikMedium text-[32px] text-cf-light-4">
      <div className="w-[389px] leading-[36px]">
        Earn up to{' '}
        <span className="text-cf-blue-4">
          <CustomNumberFlow className="h-[36px]" value={apyToText(apy)} />
        </span>{' '}
        APY by supplying liquidity
      </div>
      <div className="flex gap-x-4">
        <LpFundAccountButton
          icon={<ArrowRightIcon />}
          iconPos="right"
          disabled={isLendingPoolsLoading}
          onClick={() => openSupplyModal(chainflipAssetMap.Usdc)}
        >
          Supply
        </LpFundAccountButton>
      </div>
    </div>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-3">
      <Card
        loading={isLendingPoolsLoading}
        icon={<ArrowIcon direction="up" className="text-cf-light-2" />}
        title="Total Supplied"
        tooltip="The total value of funds supplied by lenders across all markets."
        footer="Current"
      >
        {formatUsdValue(totalSuppliedUsd)}
      </Card>
      <Card
        loading={isAllTimeStatsLoading}
        icon={<BorrowIcon className="text-cf-light-2" />}
        title="Total Borrowed Ever"
        tooltip="The total value of supplied assets that have ever been lent out to borrowers."
        footer="All Time"
      >
        {formatUsdValue(allTimeBorrowedUsd)}
      </Card>
      <Card
        loading={isAllTimeStatsLoading}
        icon={<MoneyBagIcon className="text-cf-light-2" />}
        title="Earned Fees"
        tooltip="The total supply-side fees earned by all lenders."
        footer="All Time"
      >
        {formatUsdValue(totalFeesUsd)}
      </Card>
    </div>
  </>
);

export default function MySupplyPositionContent({ onBorrowClick }: { onBorrowClick?: () => void }) {
  const router = useRouter();
  const {
    value: supplyModalOpen,
    setTrue: setSupplyModalOpened,
    setFalse: setSupplyModalClosed,
  } = useBoolean(false);
  const {
    value: removeSupplyModalOpen,
    setTrue: setRemoveSupplyModalOpened,
    setFalse: setRemoveSupplyModalClosed,
  } = useBoolean(false);
  const [selectedToken, setSelectedToken] = useState<ChainflipToken>(chainflipAssetMap.Usdc);
  const { selectedAccount } = usePolkadot(true);
  const { data: positions, isLoading: isPositionsLoading } = useLpLendingPositions({
    idSs58: selectedAccount.idSs58,
  });
  const { maxApy, totalSuppliedUsd, isLoading: isLendingPoolsLoading } = useLendingPools();
  const {
    totalPoolFeesUsd,
    allTimeBorrowedUsd,
    isLoading: isAllTimeLendingStatsLoading,
  } = useAllTimeLendingStats();

  const openSupplyModal = (token: ChainflipToken) => {
    setSelectedToken(token);
    setSupplyModalOpened();
  };

  const openRemoveSupplyModal = (token: ChainflipToken) => {
    setSelectedToken(token);
    setRemoveSupplyModalOpened();
  };

  const content =
    positions.length > 0 ? (
      <>
        <div className="flex justify-between font-aeonikMedium">
          <div className="text-24 text-cf-white">My Position</div>
          <div className="flex gap-x-4">
            <LpFundAccountButton
              icon={<ArrowRightIcon />}
              iconPos="right"
              onClick={() => openSupplyModal(chainflipAssetMap.Usdc)}
            >
              Supply
            </LpFundAccountButton>
            <div
              className="flex h-[40px] w-[40px] cursor-pointer items-center justify-center rounded-md border border-cf-gray-4 bg-cf-gray-3-5 text-cf-light-3 hover:border-cf-gray-5 hover:bg-cf-gray-4"
              onClick={() => {
                router.push(`/lending/history/supply`);
              }}
            >
              <DocHistoryIcon />
            </div>
          </div>
        </div>
        <SupplyPosition
          lpIdSs58={selectedAccount.idSs58}
          openSupplyModal={openSupplyModal}
          openRemoveSupplyModal={openRemoveSupplyModal}
        />
      </>
    ) : (
      <NoLendingPositions
        openSupplyModal={openSupplyModal}
        apy={maxApy}
        isAllTimeStatsLoading={isAllTimeLendingStatsLoading}
        isLendingPoolsLoading={isLendingPoolsLoading}
        totalFeesUsd={Number(totalPoolFeesUsd ?? 0)}
        allTimeBorrowedUsd={Number(allTimeBorrowedUsd ?? 0)}
        totalSuppliedUsd={totalSuppliedUsd ?? 0}
      />
    );

  const RowButton = useCallback(
    // eslint-disable-next-line react/no-unused-prop-types
    ({ asset }: { asset: ChainflipAsset }) => (
      <LpFundAccountButton
        size="small"
        type="primary-standard"
        onClick={(e) => {
          e.stopPropagation();
          openSupplyModal(chainflipAssetMap[asset]);
        }}
        disabled={isPositionsLoading || isLendingPoolsLoading}
      >
        Supply
      </LpFundAccountButton>
    ),
    [isPositionsLoading, isLendingPoolsLoading],
  );

  return (
    <div className="flex flex-col gap-y-6">
      <RemoveSupplyModal
        open={removeSupplyModalOpen}
        onClose={() => {
          setRemoveSupplyModalClosed();
        }}
        selectedToken={selectedToken}
        setSelectedToken={setSelectedToken}
      />
      <SupplyModal
        open={supplyModalOpen}
        onClose={() => {
          setSupplyModalClosed();
        }}
        selectedToken={selectedToken}
        setSelectedToken={setSelectedToken}
        onBorrowClick={onBorrowClick}
      />
      {isPositionsLoading ? <LoadingSpinner className="self-center" /> : content}
      <div className="font-aeonikMedium text-24 text-cf-white">Markets</div>
      <LendingPoolsTable
        Button={RowButton}
        onRowClick={(chainflipAsset) => router.push(`/lending/markets/${chainflipAsset}`)}
      />
    </div>
  );
}
